import { bitrixAjaxFetch } from './bitrix-ajax';
import { refreshEventStatusLabels } from './event-status';

export type AjaxLoadMoreConfig = {
  gridSelector: string;
  wrapSelector: string;
  btnSelector: string;
  cardSelector?: string;
  initFlag?: string;
  modalsContainerSelector?: string;
};

export function replaceLoadMoreWrap(scope: ParentNode, wrapSelector: string, source: ParentNode): void {
  const currentWrap = scope.querySelector<HTMLElement>(wrapSelector);
  const nextWrap = source.querySelector<HTMLElement>(wrapSelector);

  if (!currentWrap) return;

  if (nextWrap) {
    currentWrap.replaceWith(nextWrap.cloneNode(true));
    return;
  }

  currentWrap.hidden = true;
  currentWrap.dataset.loadMoreExhausted = 'true';
}

export type ListLoadMoreVisibilityConfig = {
  gridSelector: string;
  wrapSelector: string;
  cardSelector: string;
};

/** Скрывает «Показать ещё», если в списке нет карточек (пустой поиск / фильтр). */
export function syncListLoadMoreVisibility(
  root: ParentNode,
  config: ListLoadMoreVisibilityConfig,
): void {
  const grid = root.querySelector<HTMLElement>(config.gridSelector);
  const wrap = root.querySelector<HTMLElement>(config.wrapSelector);
  if (!wrap) return;

  const cardCount = grid?.querySelectorAll(config.cardSelector).length ?? 0;
  if (cardCount === 0) {
    wrap.hidden = true;
    delete wrap.dataset.loadMoreExhausted;
  }
}

export function initAjaxLoadMore(root: ParentNode, config: AjaxLoadMoreConfig): void {
  const scope = root;
  const initFlag = config.initFlag ?? 'ajaxLoadMoreBound';
  const cardSelector = config.cardSelector ?? 'article';

  const getElements = (): {
    grid: HTMLElement;
    wrap: HTMLElement;
    btn: HTMLButtonElement;
  } | null => {
    const grid = scope.querySelector<HTMLElement>(config.gridSelector);
    const wrap = scope.querySelector<HTMLElement>(config.wrapSelector);
    const btn = scope.querySelector<HTMLButtonElement>(config.btnSelector);

    if (!grid || !wrap || !btn) return null;
    return { grid, wrap, btn };
  };

  const initial = getElements();
  if (!initial) return;

  syncListLoadMoreVisibility(scope, {
    gridSelector: config.gridSelector,
    wrapSelector: config.wrapSelector,
    cardSelector,
  });

  if (!initial.btn.getAttribute('data-url')) {
    initial.wrap.hidden = true;
    initial.wrap.dataset.loadMoreExhausted = 'true';
    return;
  }

  if (scope instanceof HTMLElement && scope.dataset[initFlag] === 'true') {
    return;
  }
  if (scope instanceof HTMLElement) {
    scope.dataset[initFlag] = 'true';
  }

  const loadNext = async (btn: HTMLButtonElement): Promise<void> => {
    const url = btn.getAttribute('data-url');
    const elements = getElements();
    if (!url || !elements) return;

    const { grid, wrap } = elements;

    wrap.classList.add('is-loading');
    btn.disabled = true;

    try {
      const response = await bitrixAjaxFetch(url, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`Load more failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      doc.querySelectorAll(cardSelector).forEach((card) => {
        grid.append(card.cloneNode(true));
      });

      if (config.modalsContainerSelector) {
        const modalsContainer = scope.querySelector(config.modalsContainerSelector);
        const sourceModals = doc.querySelector(config.modalsContainerSelector);
        if (modalsContainer && sourceModals) {
          sourceModals.querySelectorAll('.modal').forEach((modal) => {
            modalsContainer.append(modal.cloneNode(true));
          });
        }
      }

      replaceLoadMoreWrap(scope, config.wrapSelector, doc);
      refreshEventStatusLabels(scope);
    } catch (error) {
      console.error(error);
    } finally {
      const updated = getElements();
      if (updated) {
        updated.wrap.classList.remove('is-loading');
        updated.btn.disabled = false;
      }
    }
  };

  scope.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const btn = target.closest<HTMLButtonElement>(config.btnSelector);
    if (!btn || !scope.contains(btn)) return;

    event.preventDefault();
    void loadNext(btn);
  });
}
